import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Modal } from '@/components/ui/Modal';
import { Button } from '@/components/ui/Button';
import { Textarea } from '@/components/ui/Textarea';
import { Input } from '@/components/ui/Input';
import { Icon } from '@/components/ui/Icon';
import { Badge } from '@/components/ui/Badge';
import { ChannelMultiSelect, ChannelOption } from '@/components/ui/ChannelMultiSelect';
import { ICONS } from '@/constants/icons';
import { cn } from '@/utils/cn';
import { getChannelAvatar } from '@/utils/channel-avatar';
import { getPlatformInfo, isSocialPublishTarget } from '@/constants/platforms';
import { useChannels } from '@/hooks/useChannels';
import { useGenerateSocialFromBlog } from '@/hooks/useBlog';
import { GeneratedSocialPost, SocialGenerationTone } from '@/services/blog.api';
import { formatApiError, isGlobalGatingHandledError } from '@/services/api';

/**
 * "Generate social posts from this blog?" flow (SPEC.md Phase 2, decision C).
 *
 * Shown after a blog post has been published/scheduled to WordPress. Lets
 * the user pick a tone, optional custom instructions, target social
 * channels, and how many drafts to generate — then calls ÄÄ's AI stack to
 * create native draft `Post`s on those channels (never auto-scheduled).
 */

interface ToneOption {
  value: SocialGenerationTone;
  label: string;
  description: string;
}

// Descriptions mirror Kristin's original tone picker (reference Apps Script,
// SPEC.md decision B — reproduce behaviour, not code) so the UX she's used
// to carries over 1:1.
const TONE_OPTIONS: ToneOption[] = [
  { value: 'professional', label: 'Professional', description: 'Polished & authoritative — thought leadership, B2B.' },
  { value: 'casual', label: 'Casual', description: 'Conversational & friendly — like talking to a friend.' },
  { value: 'edgy', label: 'Edgy', description: 'Bold & provocative — challenges assumptions, stands out.' },
  { value: 'inspirational', label: 'Inspirational', description: 'Uplifting & motivational — empowers the reader to act.' },
];

const MIN_COUNT = 3;
const MAX_COUNT = 10;
const DEFAULT_COUNT = 5;

interface BlogGenerateSocialModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  blogPostId: number;
}

export const BlogGenerateSocialModal = ({ open, onOpenChange, blogPostId }: BlogGenerateSocialModalProps) => {
  const { t } = useTranslation('common');
  const navigate = useNavigate();

  const { data: channels, isLoading: isLoadingChannels } = useChannels();
  const generateMutation = useGenerateSocialFromBlog(blogPostId);

  const [tone, setTone] = useState<SocialGenerationTone>('professional');
  const [customInstructions, setCustomInstructions] = useState('');
  const [channelIds, setChannelIds] = useState<number[]>([]);
  const [count, setCount] = useState(DEFAULT_COUNT);
  const [preselected, setPreselected] = useState(false);
  const [result, setResult] = useState<{ createdPosts: GeneratedSocialPost[]; tokensUsed: number } | null>(null);

  // Reset all local state whenever the modal is (re)opened for a fresh run.
  useEffect(() => {
    if (open) return;
    setTone('professional');
    setCustomInstructions('');
    setChannelIds([]);
    setCount(DEFAULT_COUNT);
    setPreselected(false);
    setResult(null);
    generateMutation.reset();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Publishable SOCIAL channels only — exclude WordPress/Canva/GDrive, which
  // aren't social-post destinations (mirrors ChannelSelector's filter).
  const socialChannels = useMemo(
    () => (channels ?? []).filter((c) => isSocialPublishTarget(c.platform) && c.isActive),
    [channels]
  );

  const channelOptions: ChannelOption[] = useMemo(
    () =>
      socialChannels.map((c) => ({
        id: c.id,
        name: c.platformUsername || `Channel ${c.id}`,
        platform: c.platform,
        avatarUrl: getChannelAvatar(c.metadata),
      })),
    [socialChannels]
  );

  // Preselect once channels load: LinkedIn/Instagram if present, else all.
  useEffect(() => {
    if (preselected || socialChannels.length === 0) return;
    const preferred = socialChannels.filter((c) => c.platform === 'LINKEDIN' || c.platform === 'INSTAGRAM');
    setChannelIds((preferred.length > 0 ? preferred : socialChannels).map((c) => c.id));
    setPreselected(true);
  }, [socialChannels, preselected]);

  // A quota/feature upsell already opened globally for this error: show it once, not inline too.
  const apiError =
    generateMutation.error && !isGlobalGatingHandledError(generateMutation.error)
      ? formatApiError(generateMutation.error)
      : null;
  const isOutOfTokens = apiError?.code === 'INSUFFICIENT_AI_TOKENS' || apiError?.statusCode === 402;
  const isFeatureGated = apiError?.statusCode === 403 && !isOutOfTokens;

  const canSubmit = channelIds.length > 0 && count >= MIN_COUNT && count <= MAX_COUNT;

  const handleGenerate = async () => {
    try {
      const data = await generateMutation.mutateAsync({
        tone,
        customInstructions: customInstructions.trim() || undefined,
        channelIds,
        count,
      });
      setResult(data);
    } catch {
      // Error state is rendered from generateMutation.error below.
    }
  };

  const handleClose = () => {
    if (generateMutation.isPending) return; // don't allow closing mid-generation
    onOpenChange(false);
  };

  // Group created posts by platform for the success summary.
  const groupedByPlatform = useMemo(() => {
    if (!result) return [];
    const map = new Map<string, GeneratedSocialPost[]>();
    for (const post of result.createdPosts) {
      const list = map.get(post.platform) ?? [];
      list.push(post);
      map.set(post.platform, list);
    }
    return Array.from(map.entries());
  }, [result]);

  return (
    <Modal
      open={open}
      onOpenChange={handleClose}
      title={
        result
          ? t('blog_social_modal_success_title', { defaultValue: 'Social drafts created' })
          : t('blog_social_modal_title', { defaultValue: 'Generate social posts from this blog?' })
      }
      size="lg"
      preventClose={generateMutation.isPending}
    >
      {result ? (
        // ─── Success summary ────────────────────────────────────────────
        <div className="space-y-4">
          <div className="flex items-start gap-3 rounded-xl border border-green-200 bg-green-50 p-4">
            <Icon name={ICONS.CHECK_CIRCLE} size="md" color="text-green-600" ariaHidden className="mt-0.5 flex-shrink-0" />
            <div>
              <p className="text-sm font-medium text-green-900">
                {t('blog_social_success_count', {
                  defaultValue: `${result.createdPosts.length} draft${result.createdPosts.length === 1 ? '' : 's'} created`,
                  count: result.createdPosts.length,
                })}
              </p>
              <p className="text-xs text-green-700 mt-0.5">
                {t('blog_social_success_hint', {
                  defaultValue: 'They landed as drafts — nothing was scheduled or published automatically. Review and edit before sending.',
                })}
              </p>
            </div>
          </div>

          <div className="space-y-3">
            {groupedByPlatform.map(([platform, posts]) => {
              const info = getPlatformInfo(platform);
              return (
                <div key={platform} className="rounded-xl border border-gray-200 p-3">
                  <div className="flex items-center gap-2 mb-2">
                    <span
                      className="w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0"
                      style={{ backgroundColor: info.color }}
                    >
                      <Icon name={ICONS.SHARE} size="xs" color="text-white" ariaHidden />
                    </span>
                    <span className="text-sm font-semibold text-gray-900">{info.name}</span>
                    <Badge variant="neutral">{posts.length}</Badge>
                  </div>
                  <ul className="space-y-1.5">
                    {posts.map((post) => (
                      <li key={post.postId} className="text-xs text-gray-600 line-clamp-2 pl-7">
                        {post.caption}
                      </li>
                    ))}
                  </ul>
                </div>
              );
            })}
          </div>

          <div className="flex flex-col sm:flex-row gap-2 pt-2 border-t border-gray-100">
            <Button variant="outline" onClick={handleClose} className="flex-1">
              {t('blog_social_success_close', { defaultValue: 'Close' })}
            </Button>
            <Button variant="primary" onClick={() => navigate('/calendar')} className="flex-1">
              <Icon name={ICONS.CALENDAR} size="sm" className="mr-1.5" ariaHidden />
              {t('blog_social_success_view_drafts', { defaultValue: 'View drafts in Calendar' })}
            </Button>
          </div>
        </div>
      ) : (
        // ─── Configuration form ─────────────────────────────────────────
        <div className="space-y-5">
          {/* Tone picker */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {t('blog_social_tone_label', { defaultValue: 'Tone' })}
            </label>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {TONE_OPTIONS.map((opt) => (
                <button
                  key={opt.value}
                  type="button"
                  onClick={() => setTone(opt.value)}
                  disabled={generateMutation.isPending}
                  className={cn(
                    'p-3 rounded-xl border-2 text-left transition-all disabled:opacity-50 disabled:cursor-not-allowed',
                    tone === opt.value ? 'border-primary-500 bg-primary-50' : 'border-gray-200 hover:border-gray-300'
                  )}
                >
                  <span
                    className={cn(
                      'text-sm font-semibold block',
                      tone === opt.value ? 'text-primary-700' : 'text-gray-800'
                    )}
                  >
                    {opt.label}
                  </span>
                  <span className="text-xs text-gray-500 mt-0.5 block">{opt.description}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Custom instructions */}
          <div>
            <label htmlFor="blog-social-instructions" className="block text-sm font-medium text-gray-700 mb-1.5">
              {t('blog_social_instructions_label', { defaultValue: 'Custom instructions (optional)' })}
            </label>
            <Textarea
              id="blog-social-instructions"
              value={customInstructions}
              onChange={(e) => setCustomInstructions(e.target.value)}
              placeholder={t('blog_social_instructions_placeholder', {
                defaultValue: 'e.g. "focus on the career-change angle" or "add a CTA to book a call"',
              })}
              rows={2}
              maxLength={1000}
              disabled={generateMutation.isPending}
            />
          </div>

          {/* Channels + count */}
          <div className="grid grid-cols-1 sm:grid-cols-[1fr_auto] gap-4">
            <ChannelMultiSelect
              label={t('blog_social_channels_label', { defaultValue: 'Target channels' })}
              value={channelIds}
              onChange={setChannelIds}
              options={channelOptions}
              isLoading={isLoadingChannels}
              disabled={generateMutation.isPending}
              placeholder={t('blog_social_channels_placeholder', { defaultValue: 'Select social channels' })}
            />
            <div className="w-full sm:w-28">
              <label htmlFor="blog-social-count" className="block text-sm font-medium text-gray-700 mb-1.5">
                {t('blog_social_count_label', { defaultValue: 'Count' })}
              </label>
              <Input
                id="blog-social-count"
                type="number"
                min={MIN_COUNT}
                max={MAX_COUNT}
                value={count}
                onChange={(e) => {
                  const next = parseInt(e.target.value, 10);
                  if (Number.isNaN(next)) return;
                  setCount(Math.min(MAX_COUNT, Math.max(MIN_COUNT, next)));
                }}
                disabled={generateMutation.isPending}
              />
            </div>
          </div>

          {!isLoadingChannels && channelOptions.length === 0 && (
            <div className="flex items-start gap-2 rounded-lg bg-amber-50 border border-amber-200 p-3">
              <Icon name={ICONS.WARNING} size="sm" color="text-amber-600" ariaHidden className="mt-0.5 flex-shrink-0" />
              <p className="text-xs text-amber-800">
                {t('blog_social_no_channels', {
                  defaultValue: 'Connect a social channel (LinkedIn, Instagram, etc.) before generating posts.',
                })}
              </p>
            </div>
          )}

          {/* Error state */}
          {apiError && (
            <div className="flex items-start gap-2 rounded-lg bg-red-50 border border-red-200 p-3">
              <Icon name={ICONS.WARNING} size="sm" color="text-red-600" ariaHidden className="mt-0.5 flex-shrink-0" />
              <div>
                <p className="text-sm font-medium text-red-800">
                  {isOutOfTokens
                    ? t('blog_social_error_tokens_title', { defaultValue: "You've used all your AI tokens for this month" })
                    : isFeatureGated
                      ? t('blog_social_error_gated_title', { defaultValue: 'This feature is not available on your plan' })
                      : t('blog_social_error_generic_title', { defaultValue: 'Could not generate social posts' })}
                </p>
                <p className="text-xs text-red-700 mt-0.5">{apiError.message}</p>
              </div>
            </div>
          )}

          {/* Loading state */}
          {generateMutation.isPending && (
            <div className="flex items-center gap-3 rounded-xl border border-primary-200 bg-primary-50 p-4">
              <div className="h-6 w-6 flex-shrink-0 animate-spin rounded-full border-2 border-primary-200 border-t-primary-600" />
              <div>
                <p className="text-sm font-medium text-primary-900">
                  {t('blog_social_generating_title', { defaultValue: 'Generating social posts…' })}
                </p>
                <p className="text-xs text-primary-700 mt-0.5">
                  {t('blog_social_generating_hint', { defaultValue: 'This can take up to ~20 seconds — please keep this open.' })}
                </p>
              </div>
            </div>
          )}

          <div className="flex justify-end gap-3 pt-2 border-t border-gray-100">
            <Button variant="outline" onClick={handleClose} disabled={generateMutation.isPending}>
              {t('blog_social_cancel', { defaultValue: 'Cancel' })}
            </Button>
            <Button
              variant="primary"
              onClick={handleGenerate}
              disabled={!canSubmit || generateMutation.isPending}
              loading={generateMutation.isPending}
            >
              <Icon name={ICONS.SPARKLES} size="sm" className="mr-1.5" ariaHidden />
              {t('blog_social_submit', { defaultValue: 'Generate Social Posts' })}
            </Button>
          </div>
        </div>
      )}
    </Modal>
  );
};

export default BlogGenerateSocialModal;
